import { itemsApi } from '@/api/items.api';
import { Tip, configText } from '@/components/admin/ui';
import { PadlockIcon, PencilIcon } from '@/components/common/icons';
import type { ItemModel } from '@/types/categories.types';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/** The prototype's example matches of the seeded regex items, keyed by item id. */
const REGEX_EXAMPLES: Record<string, string[]> = {
  // biome-ignore lint/style/useNamingConvention: seeded item ids
  birth_number: ['780512/3415', '8551233418'],
  email: ['j.vavra@bvk.cz'],
  phone: ['+420 602 118 340', '602118340'],
  // biome-ignore lint/style/useNamingConvention: seeded item ids
  id_card: ['OP č. 123456789'],
  ico: ['IČO: 46347275'],
  dic: ['CZ46347275'],
  amount: ['4 180 000 Kč', '12 500,50 EUR'],
  // biome-ignore lint/style/useNamingConvention: seeded item ids
  bank_account: ['19-2000145399/0800', 'CZ65 0800 0000 1920 0014 5399'],
  hostname: ['vdc-scada-03.bvk.local'],
  ip: ['10.12.4.21', '192.168.0.0/24'],
  // biome-ignore lint/style/useNamingConvention: seeded item ids
  software_version: ['v14.2', '2.10.3'],
  // biome-ignore lint/style/useNamingConvention: seeded item ids
  gh_token: ['ghp_R7tQ9xLm4Kd2Vb8Hn'],
  password: ['heslo: Bvk!2026#scada', 'pwd=Tajne123'],
  // biome-ignore lint/style/useNamingConvention: seeded item ids
  private_key: ['-----BEGIN RSA PRIVATE KEY-----'],
};

/**
 * Examples the item's *current* regex really matches, asked of the backend once per pattern: an
 * admin may have changed the seed, and an example that no longer matches would mislead.
 */
const verified = new Map<string, Promise<string[]>>();
const verifiedExamples = (item: ItemModel): Promise<string[]> => {
  const examples = REGEX_EXAMPLES[item.id];
  if (!examples || item.type !== 'regex' || !item.config) return Promise.resolve([]);
  const key = `${item.id}\u0000${item.config}`;
  let pending = verified.get(key);
  if (!pending) {
    pending = Promise.all(
      examples.map((example) =>
        itemsApi
          .regexTest(item.config, example)
          .then((result) => (!result.error && result.matches.length ? example : null))
          .catch(() => null)
      )
    ).then((list) => list.filter((example): example is string => example !== null));
    verified.set(key, pending);
  }
  return pending;
};

interface ConfigButtonProps {
  item: ItemModel;
  onOpen: () => void;
  /** The categories view draws it white, the items table on the wash background. */
  tone: 'white' | 'wash';
}

/**
 * The configuration cell: up to three lines of the prompt, regex or values, a pencil (editable)
 * or a padlock (read-only), a click opens the editor. A regex item shows "Příklad shody" on hover.
 */
export const ConfigButton: FC<ConfigButtonProps> = ({ item, onOpen, tone }) => {
  const { t } = useTranslation();
  const [examples, setExamples] = useState<string[]>([]);
  const text = configText(item);
  const isLlm = item.type === 'LLM';

  return (
    <Tip
      className="min-w-0"
      disabled={examples.length === 0}
      content={
        <span className="flex flex-col gap-[5px]">
          <span className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-[#a9bccd]">
            {t('admin.example.title')}
          </span>
          {examples.map((example) => (
            <span key={example} className="self-start bg-white/10 px-1.5 py-0.5 font-mono text-[11.5px] text-white">
              {example}
            </span>
          ))}
          <span className="text-[10.5px] text-[#a9bccd]">{t('admin.example.hint')}</span>
        </span>
      }
    >
      <button
        type="button"
        onClick={onOpen}
        onMouseEnter={() => {
          verifiedExamples(item).then(setExamples);
        }}
        aria-label={`${t(`admin.editor.kind.${item.type}`)}: ${item.name}`}
        className={`flex w-full cursor-pointer items-start gap-2 rounded border text-left font-[inherit] hover:border-primary hover:bg-white ${
          tone === 'wash' ? 'border-control bg-wash px-2 py-1.5' : 'border-line bg-white px-2 py-[5px]'
        }`}
      >
        <span
          className={`line-clamp-3 min-w-0 flex-1 whitespace-pre-wrap break-words leading-[1.5] ${
            isLlm ? 'font-[inherit] text-xs' : 'font-mono text-[11px]'
          } ${text ? 'text-ink' : 'text-faint'}`}
        >
          {text || t(`admin.configPlaceholder.${item.type}`)}
        </span>
        {item.editable ? (
          <PencilIcon size={12} stroke="#6d7f91" className="mt-0.5 flex-none" />
        ) : (
          <PadlockIcon size={12} stroke="#93a5b5" className="mt-0.5 flex-none" />
        )}
      </button>
    </Tip>
  );
};
